import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { fetchStatementIssues } from '../api/taskInbox';
import { useAuth } from '../context/AuthContext';

const TEACHER_LINKS = [
  {
    to: '/teacher/task-inbox',
    title: 'Очередь задач',
    description: 'Моя очередь и задачи с проблемой в условии.',
  },
  {
    to: '/teacher/ege-families',
    title: 'Раскладка по семьям',
    description: 'Разложить задачи банка по семьям внутри типа КИМ.',
  },
  {
    to: '/teacher/ege-map',
    title: 'Карта ЕГЭ',
    description: 'Просмотр журнальной карты семей и ступеней (только чтение).',
  },
  {
    to: '/teacher/tasks',
    title: 'Конструктор задач',
    description: 'Создание и правка задач.',
  },
  {
    to: '/teacher/tasks/import',
    title: 'Пакетный импорт',
    description: 'Массовая загрузка задач из JSON.',
  },
  {
    to: '/teacher/tags',
    title: 'Теги',
    description: 'Справочник тегов каталога.',
  },
  {
    to: '/catalog',
    title: 'Каталог',
    description: 'Просмотр банка задач с фильтрами.',
  },
];

const ADMIN_LINKS = [
  {
    to: '/admin/task-cleanup',
    title: 'Очистка банка',
    description: 'Жёсткое удаление лишних задач по типу КИМ.',
  },
  {
    to: '/admin/topic-programs',
    title: 'Программы (темы)',
    description: 'Управление программами тем в меню и каталоге.',
  },
];

export default function TeacherToolsPage() {
  const { user } = useAuth();
  const isSiteAdmin = Boolean(user?.is_staff || user?.is_admin);
  const [inboxCount, setInboxCount] = useState(null);
  const [issueCount, setIssueCount] = useState(null);
  const [inboxError, setInboxError] = useState('');

  const loadCounts = useCallback(async () => {
    setInboxError('');
    try {
      const [inboxRes, issuesRes] = await Promise.all([
        api.get('/teacher/task-inbox/', { params: { status: 'open' } }),
        fetchStatementIssues(),
      ]);
      setInboxCount(
        typeof inboxRes.data?.count === 'number'
          ? inboxRes.data.count
          : (inboxRes.data?.results?.length ?? 0),
      );
      setIssueCount(
        typeof issuesRes?.count === 'number'
          ? issuesRes.count
          : (issuesRes?.results?.length ?? 0),
      );
    } catch {
      setInboxCount(null);
      setIssueCount(null);
      setInboxError('Не удалось загрузить очередь.');
    }
  }, []);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  return (
    <div className="teacher-tools-page">
      <header className="page-header">
        <h1>Инструменты преподавателя</h1>
        <p className="page-subtitle text-secondary">
          Служебные страницы для банка задач, тегов и программ.
        </p>
      </header>

      <section className="teacher-tools-section" aria-label="Служебные ссылки">
        <ul className="teacher-tools-grid">
          {TEACHER_LINKS.map((link) => (
            <li key={link.to}>
              <Link to={link.to} className="teacher-tools-card">
                <span className="teacher-tools-card-title">{link.title}</span>
                <span className="teacher-tools-card-desc text-secondary">{link.description}</span>
              </Link>
            </li>
          ))}
          {isSiteAdmin
            ? ADMIN_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="teacher-tools-card">
                    <span className="teacher-tools-card-title">{link.title}</span>
                    <span className="teacher-tools-card-desc text-secondary">{link.description}</span>
                  </Link>
                </li>
              ))
            : null}
        </ul>
      </section>

      <section className="teacher-tools-section" aria-label="Очередь задач">
        <div className="teacher-tools-issues-head">
          <h2 className="teacher-tools-section-title">Очередь на рассмотрение</h2>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={loadCounts}
          >
            Обновить
          </button>
        </div>
        {inboxError ? <div className="alert alert-error">{inboxError}</div> : null}
        <p className="text-secondary">
          {inboxCount == null || issueCount == null
            ? '…'
            : (
              <>
                Моя очередь: <strong>{inboxCount}</strong>
                {' · '}
                Проблемы с условием: <strong>{issueCount}</strong>
                {(inboxCount > 0 || issueCount > 0) ? (
                  <>
                    {' · '}
                    <Link to="/teacher/task-inbox">открыть</Link>
                  </>
                ) : null}
              </>
            )}
        </p>
      </section>
    </div>
  );
}
